/**
 * DashboardStats component for displaying system-wide statistics.
 * 
 * Features:
 * - User counts (total, pending)
 * - Essay counts (total, published)
 * - Activity counts (comments, suggestions)
 * - Essays by status breakdown
 * 
 * Called by:
 * - AdminPage.tsx Dashboard tab
 */
import { Card, Row, Col, Statistic, Progress, Space, Typography, Spin, Empty } from 'antd';
import {
  FileTextOutlined,
  CommentOutlined,
  BulbOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { EssayStatus } from '@compendium/shared';
import { STATUS_LABELS } from '@compendium/shared';
import { getAdminStats } from '../../services/api';

const { Text } = Typography;

// =============================================================================
// Status Colors for Chart
// =============================================================================

const STATUS_COLORS: Record<EssayStatus, string> = {
  draft: '#8c8c8c',
  in_review: '#7c8fa6',
  revision: '#faad14',
  approved: '#52c41a',
  published: '#722ed1',
  archived: '#595959',
};

// =============================================================================
// Component
// =============================================================================

/**
 * DashboardStats component.
 * Displays admin dashboard statistics.
 */
function DashboardStats() {
  // Fetch admin stats
  const {
    data: stats,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['adminStats'],
    queryFn: getAdminStats,
    // Refresh every 30 seconds
    refetchInterval: 30000,
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Empty
        description={error instanceof Error ? error.message : 'Failed to load statistics'}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // No data
  if (!stats) {
    return (
      <Empty
        description="No statistics available"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Calculate percentages for essay status breakdown
  const totalEssays = stats.totalEssays || 1; // Avoid division by zero
  const statusPercentages = Object.entries(stats.essaysByStatus).map(([status, count]) => ({
    status: status as EssayStatus,
    count,
    percentage: Math.round((count / totalEssays) * 100),
  }));

  return (
    <div>
      {/* Main stats cards */}
      <Row gutter={[16, 16]}>
        {/* Users card */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{ background: '#1a1a1a', borderColor: '#333' }}
            hoverable
          >
            <Statistic
              title={
                <Space>
                  <TeamOutlined style={{ color: '#7c8fa6' }} />
                  <span>Total Users</span>
                </Space>
              }
              value={stats.totalUsers}
              valueStyle={{ color: '#f5f5f5' }}
            />
            {stats.pendingUsers > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Space>
                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                  <Text type="warning">{stats.pendingUsers} pending approval</Text>
                </Space>
              </div>
            )}
            {stats.blockedUsers > 0 && (
              <div style={{ marginTop: '4px' }}>
                <Space>
                  <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Text type="danger">{stats.blockedUsers} blocked</Text>
                </Space>
              </div>
            )}
          </Card>
        </Col>

        {/* Essays card */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{ background: '#1a1a1a', borderColor: '#333' }}
            hoverable
          >
            <Statistic
              title={
                <Space>
                  <FileTextOutlined style={{ color: '#52c41a' }} />
                  <span>Total Essays</span>
                </Space>
              }
              value={stats.totalEssays}
              valueStyle={{ color: '#f5f5f5' }}
            />
            {stats.publishedEssays > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Space>
                  <CheckCircleOutlined style={{ color: '#722ed1' }} />
                  <Text style={{ color: '#722ed1' }}>
                    {stats.publishedEssays} published
                  </Text>
                </Space>
              </div>
            )}
          </Card>
        </Col>

        {/* Comments card */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{ background: '#1a1a1a', borderColor: '#333' }}
            hoverable
          >
            <Statistic
              title={
                <Space>
                  <CommentOutlined style={{ color: '#13c2c2' }} />
                  <span>Total Comments</span>
                </Space>
              }
              value={stats.totalComments}
              valueStyle={{ color: '#f5f5f5' }}
            />
          </Card>
        </Col>

        {/* Suggestions card */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{ background: '#1a1a1a', borderColor: '#333' }}
            hoverable
          >
            <Statistic
              title={
                <Space>
                  <BulbOutlined style={{ color: '#eb2f96' }} />
                  <span>Total Suggestions</span>
                </Space>
              }
              value={stats.totalSuggestions}
              valueStyle={{ color: '#f5f5f5' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Essays by status breakdown */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Essays by Status</span>
          </Space>
        }
        style={{ marginTop: '24px', background: '#1a1a1a', borderColor: '#333' }}
      >
        <Row gutter={[24, 16]}>
          {statusPercentages.map(({ status, count, percentage }) => (
            <Col xs={24} sm={12} md={8} key={status}>
              <div style={{ marginBottom: '8px' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{STATUS_LABELS[status]}</Text>
                  <Text type="secondary">{count}</Text>
                </Space>
              </div>
              <Progress
                percent={percentage}
                strokeColor={STATUS_COLORS[status]}
                trailColor="#333"
                showInfo={false}
                size="small"
              />
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );
}

export default DashboardStats;
